// wabunka-backend の最寄り駅（E-2）/ Static マップ署名URL（E-3）レスポンス型。
// wabunka-protos npm パッケージへ proto が反映されたら、
// `@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience` の
// WabunkaExperienceNearestStationListResponse / WabunkaExperienceStaticMapUrlResponse へ置き換える。

export interface NearestStation {
  rank: number;
  name: string;
  latitude: number;
  longitude: number;
  // 徒歩時間（分）。取得不可の場合は undefined → 直線距離フォールバック表示
  walkMinutes?: number;
  // 徒歩距離（メートル）。取得不可の場合は undefined
  walkMeters?: number;
}

export interface NearestStationsResponse {
  items: NearestStation[];
}

// 数値化できない値（空文字・不正文字列等）は NaN になるため、有限数でなければ fallback に倒す。
// NaN をそのまま通すと walkMinutes/walkMeters の "!= null" 判定をすり抜け "NaN min walk" 等の壊れた表示になる。
const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toOptionalFiniteNumber = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  // 空文字・空白のみの文字列は Number() で 0 になるため「取得不可」（undefined）として扱う。
  if (typeof value === 'string' && value.trim() === '') return undefined;
  // number / string 以外（配列・オブジェクト等）は変換対象外＝取得不可扱い。
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const toNearestStation = (value: unknown): NearestStation | null => {
  // 配列は typeof 'object' をすり抜けるため明示的に除外する（items: [[]] を空駅として通さない）。
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  // proto3 JSON では数値が number / string どちらでも来うるため Number() で正規化する。
  // 数値化できない・NaN の場合は座標なし時と同様に安全側（fallback / undefined）へ倒す（graceful degradation）。
  return {
    rank: toFiniteNumber(o.rank),
    name: typeof o.name === 'string' ? o.name : '',
    latitude: toFiniteNumber(o.latitude),
    longitude: toFiniteNumber(o.longitude),
    walkMinutes: toOptionalFiniteNumber(o.walkMinutes),
    walkMeters: toOptionalFiniteNumber(o.walkMeters),
  };
};

/**
 * E-2 `GET /experiences/:id/nearestStations` のレスポンスを変換する。
 * null / 非オブジェクトは空配列にフォールバックする（座標なし・未キャッシュ時＝graceful degradation）。
 */
export const fromNearestStationsJson = (json: unknown): NearestStationsResponse => {
  const raw = json != null && typeof json === 'object' ? (json as { items?: unknown }).items : undefined;
  const items = Array.isArray(raw) ? raw.map(toNearestStation).filter((s): s is NearestStation => s !== null) : [];
  return { items };
};

export interface StaticMapUrlResponse {
  url: string;
}

/**
 * E-3 `GET /experiences/:id/staticMapUrl` のレスポンスを変換する。座標なしは空文字。
 */
export const fromStaticMapUrlJson = (json: unknown): StaticMapUrlResponse => {
  const url = json != null && typeof json === 'object' && typeof (json as { url?: unknown }).url === 'string' ? (json as { url: string }).url : '';
  return { url };
};
